
import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Campaign } from "@/types/campaigns";
import { getCampaignTypeDisplayName } from "@/utils/campaignTypeTranslations";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CalendarDays, Settings, Percent, Clock, Users, Tag } from "lucide-react";

interface CampaignViewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: Campaign | null;
}

export function CampaignViewModal({ open, onOpenChange, campaign }: CampaignViewModalProps) {
  if (!campaign) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Ativa';
      case 'scheduled': return 'Agendada';
      case 'completed': return 'Finalizada';
      case 'draft': return 'Rascunho';
      default: return status;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] p-0">
        <DialogHeader className="px-6 py-4 border-b bg-gray-50/50">
          <DialogTitle className="text-xl font-semibold flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Detalhes da Campanha
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="h-[calc(90vh-80px)]">
          <div className="p-6 space-y-6">
            {/* Informações Básicas */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Informações Básicas
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Nome</label>
                  <p className="text-sm text-gray-900 mt-1">{campaign.name}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <div className="mt-1">
                    <Badge className={`${getStatusColor(campaign.status)} font-medium`}>
                      {getStatusText(campaign.status)}
                    </Badge>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Produto</label>
                  <p className="text-sm text-gray-900 mt-1">
                    {campaign.product === "cashback" ? "Cashback" : "Indique e Ganhe"}
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Tipo</label>
                  <p className="text-sm text-gray-900 mt-1">
                    {getCampaignTypeDisplayName(campaign.type)}
                  </p>
                </div>
              </div>
              
              {campaign.description && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Descrição</label>
                  <p className="text-sm text-gray-700 mt-1">{campaign.description}</p>
                </div>
              )}
            </div>

            {/* Período */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                <CalendarDays className="h-5 w-5" />
                Período
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Data de Início</label>
                  <p className="text-sm text-gray-900 mt-1">{formatDate(campaign.start_date)}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Data de Término</label>
                  <p className="text-sm text-gray-900 mt-1">{formatDate(campaign.end_date)}</p>
                </div>
              </div>
            </div>

            {/* Configurações Gerais */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                <Users className="h-5 w-5" />
                Configurações Gerais
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {campaign.value && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Valor</label>
                    <p className="text-sm text-gray-900 mt-1">
                      {campaign.type === 'first_purchase' ? `${campaign.value}%` : `R$ ${campaign.value}`}
                    </p>
                  </div>
                )}
                
                {campaign.minimum_spent_amount && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Valor Mínimo de Compra</label>
                    <p className="text-sm text-gray-900 mt-1">R$ {campaign.minimum_spent_amount}</p>
                  </div>
                )}
                
                {campaign.max_coupons_per_customer && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Limite de Cashback por Cliente</label>
                    <p className="text-sm text-gray-900 mt-1">{campaign.max_coupons_per_customer}</p>
                  </div>
                )}
                
                {campaign.delay_days && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Delay (dias)</label>
                    <p className="text-sm text-gray-900 mt-1">{campaign.delay_days} dias</p>
                  </div>
                )}
              </div>
            </div>

            {/* Configurações de Cashback */}
            {campaign.type === 'accumulated_spending' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <Percent className="h-5 w-5" />
                  Configurações de Cashback
                </h3>
                
                {campaign.use_custom_cashback ? (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-purple-100 text-purple-800">Configuração Personalizada</Badge>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm font-medium text-purple-700">Porcentagem</label>
                        <p className="text-sm text-purple-900 mt-1">
                          {campaign.custom_cashback_percentage}%
                        </p>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-purple-700">Multiplicador</label>
                        <p className="text-sm text-purple-900 mt-1">
                          {campaign.custom_cashback_multiplier}x
                        </p>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-purple-700">Validade</label>
                        <p className="text-sm text-purple-900 mt-1">
                          {campaign.custom_cashback_expiration_days} dias
                        </p>
                      </div>
                    </div>
                    
                    <div className="text-sm text-purple-800 bg-purple-100 p-3 rounded">
                      <strong>Exemplo:</strong> Cliente gasta R$ 100 → recebe cupom de R$ {(100 * (campaign.custom_cashback_percentage || 0) / 100).toFixed(2)}
                      <br />
                      Para usar o cupom, precisa gastar no mínimo R$ {(100 * (campaign.custom_cashback_percentage || 0) / 100 * (campaign.custom_cashback_multiplier || 1)).toFixed(2)}
                    </div>
                  </div>
                ) : (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-100 text-blue-800">Configuração Padrão</Badge>
                    </div>
                    
                    {campaign.template_name ? (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="text-sm font-medium text-blue-700">Template</label>
                          <p className="text-sm text-blue-900 mt-1">{campaign.template_name}</p>
                        </div>
                        
                        <div>
                          <label className="text-sm font-medium text-blue-700">Porcentagem</label>
                          <p className="text-sm text-blue-900 mt-1">{campaign.template_percentage}%</p>
                        </div>
                        
                        <div>
                          <label className="text-sm font-medium text-blue-700">Multiplicador</label>
                          <p className="text-sm text-blue-900 mt-1">{campaign.template_multiplier}x</p>
                        </div>
                        
                        {campaign.template_expiration_days && (
                          <div>
                            <label className="text-sm font-medium text-blue-700">Validade</label>
                            <p className="text-sm text-blue-900 mt-1">{campaign.template_expiration_days} dias</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-blue-800">
                        Usando configuração padrão do sistema
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Informações de Sistema */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Informações do Sistema
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Criado em</label>
                  <p className="text-sm text-gray-900 mt-1">{formatDateTime(campaign.created_at)}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Última atualização</label>
                  <p className="text-sm text-gray-900 mt-1">{formatDateTime(campaign.updated_at)}</p>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
